import { type } from 'arktype'

import {
  type FinancialRate,
  financialRateSchema,
  financialRateToNumber,
  isFinancialRate,
  newFinancialRate,
  ratesEqual,
  rateToPercentString,
  X0,
} from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'
import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { type Result, success } from '@orus.eu/result'
import type { Language } from '@orus.eu/translations'

import {
  AbstractDimension,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  getParmeterlessKeyTranslations,
} from './abstract-dimension.ts'
import { BooleanDimension } from './boolean-dimension.ts'

// the default value is not a valid rate, it’s just a way to indicate that the installment fee rate is set by default
export const installmentFeeRateCodes = ['discounted', 'standard', 'advanced'] as const

export type InstallmentFeeRateCode = (typeof installmentFeeRateCodes)[number]

export const installmentFeeRateCodeSchema = type('===', ...installmentFeeRateCodes)

export const installmentFeeRateSchema = type({
  code: installmentFeeRateCodeSchema,
  rate: financialRateSchema,
})

const DISCOUNTED_RATE = newFinancialRate('0.05')
const STANDARD_RATE = newFinancialRate('0.1')
const ADVANCED_RATE = newFinancialRate('0.15')

export const installmentFeeRate: Record<InstallmentFeeRateCode, { rate: FinancialRate }> = {
  discounted: { rate: DISCOUNTED_RATE },
  standard: { rate: STANDARD_RATE },
  advanced: { rate: ADVANCED_RATE },
}

export const installmentFeeLabels: { [key in InstallmentFeeRateCode]: string } = {
  discounted: `+ 5${nbsp}%`,
  standard: `+ 10${nbsp}%`,
  advanced: `+ 15${nbsp}%`,
}

export const installmentFeeRateValues = Object.keys(installmentFeeRate) as string[]

export type InstallmentFeeRates = typeof installmentFeeRateSchema.infer

export const INSTALLMENT_FEES_DISCOUNTED = {
  code: 'discounted',
  rate: DISCOUNTED_RATE,
} as const

export const INSTALLMENT_FEES_STANDARD = {
  code: 'standard',
  rate: STANDARD_RATE,
} as const

export const INSTALLMENT_FEES_ADVANCED = {
  code: 'advanced',
  rate: ADVANCED_RATE,
} as const

export const allInstallmentFeeRate: readonly InstallmentFeeRates[] = Object.entries(installmentFeeRate).map(
  ([code, def]) => ({
    code,
    rate: def.rate,
  }),
) as readonly InstallmentFeeRates[]

export function isInstallmentFeeRate(value: unknown): value is InstallmentFeeRates {
  return installmentFeeRateSchema.allows(value)
}

/**
 * Check if the value is a valid InstallmentFeeRates, 'none', or 'default'.
 */
export function isInstallmentFeeRateWithDefaultFullyFilled(value: unknown): boolean {
  return value === 'none' || value === 'default' || isInstallmentFeeRate(value)
}

export function getInstallmentFeeRateByCode(code: string): InstallmentFeeRates | undefined {
  return allInstallmentFeeRate.find((installmentFeeRate) => installmentFeeRate.code === code) ?? undefined
}

export function getInstallmentFeeRateByRate(rate: FinancialRate): InstallmentFeeRates | undefined {
  return allInstallmentFeeRate.find((installmentFeeRate) => ratesEqual(installmentFeeRate.rate, rate)) ?? undefined
}

export class InstallmentFeeRateWithDefaultDimension<NAME extends string> extends AbstractDimension<
  NAME,
  InstallmentFeeRates | 'none' | 'default'
> {
  override readonly placeholders = getParmeterlessKeyTranslations('placeholder_no_fee')

  override validateData(value: unknown): Result<InstallmentFeeRates | 'none' | 'default', DimensionValidationProblem> {
    if (value === 'none' || value === 'default') return success(value)

    return validateInstallmentFeeRate(value, this.name)
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = getTrackingMessageFieldAdapter(
    this.displayNames,
    this.hints,
  )

  override formatToString(value: InstallmentFeeRates | 'none' | 'default'): string | undefined {
    return value === 'none'
      ? 'none'
      : value === 'default'
        ? 'default'
        : `${value.code} - ${rateToPercentString(value.rate, { decimals: 2 })}`
  }
}

export class InstallmentFeeRateDimension<NAME extends string> extends AbstractDimension<
  NAME,
  InstallmentFeeRates | 'none'
> {
  override readonly placeholders = getParmeterlessKeyTranslations('placeholder_no_fee')

  override validateData(value: unknown): Result<InstallmentFeeRates | 'none', DimensionValidationProblem> {
    if (value === 'none') return success(value)

    return validateInstallmentFeeRate(value, this.name)
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = getTrackingMessageFieldAdapter(
    this.displayNames,
    this.hints,
  )

  override formatToString(value: InstallmentFeeRates | 'none'): string | undefined {
    return value === 'none' ? 'none' : `${value.code} - ${rateToPercentString(value.rate, { decimals: 2 })}`
  }
}

export class FinancialRateDimensionWithRetrocompatibleInstallmentFeeRateDimension<
  NAME extends string,
> extends AbstractDimension<NAME, FinancialRate | InstallmentFeeRates | 'none'> {
  override validateData(
    value: unknown,
  ): Result<FinancialRate | InstallmentFeeRates | 'none', DimensionValidationProblem> {
    if (value === 'none') return success(value)

    if (typeof value === 'object' && value !== null && 'code' in value) {
      return validateInstallmentFeeRate(value, this.name)
    }

    const result = financialRateSchema(value)
    if (result instanceof type.errors) {
      return dimensionValidationFailure(`Field ${this.name} is not valid`)
    }
    return success(result)
  }

  formatToString(value: FinancialRate | InstallmentFeeRates | 'none'): string | undefined {
    return value === 'none'
      ? 'none'
      : 'code' in value
        ? `${value.code} - ${rateToPercentString(value.rate, { decimals: 2 })}`
        : rateToPercentString(value, { decimals: 2 })
  }
}

export function getFinancialRateWithRetrocompatibleInstallmentFeeRate(
  rate: FinancialRate | InstallmentFeeRates | 'none',
): FinancialRate {
  if (rate === 'none') return X0
  return isFinancialRate(rate) ? rate : rate.rate
}

function validateInstallmentFeeRate(
  value: unknown,
  name: string,
): Result<InstallmentFeeRates | 'none', DimensionValidationProblem> {
  const parsedResult = installmentFeeRateSchema(value)

  if (parsedResult instanceof type.errors) {
    return dimensionValidationFailure(`Field ${name} is not a valid installment fees rate`)
  }

  const installmentFeeRateResult = parsedResult

  const { code, rate } = installmentFeeRateResult

  const installmentFeeRateDefinition = installmentFeeRate[code as InstallmentFeeRateCode]

  if (!installmentFeeRateDefinition || !ratesEqual(installmentFeeRateDefinition.rate, rate)) {
    return dimensionValidationFailure(`Field ${name} is not a valid discount`)
  }

  return success(installmentFeeRateResult)
}

function getTrackingMessageFieldAdapter(
  displayNames: Record<Language, string>,
  hints: Record<Language, string> | undefined,
): DimensionTrackingMessageFieldAdapter {
  return {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<number> = {
        title: displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (hints) {
        valueDocumentation.detailedExplanationMd = hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.number(valueDocumentation))
    },
    convertStateValueToTrackingValue: (
      stateValue: InstallmentFeeRates | 'none' | 'default' | null | undefined,
    ): number | null => {
      if (stateValue === 'none') return 0
      if (!stateValue || stateValue === 'default') return null
      return financialRateToNumber(stateValue.rate)
    },
  }
}

export const installmentFeeRateDimension = new InstallmentFeeRateDimension({
  name: 'installmentFeeRate',
  displayKeys: { name: 'dimension_name_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
} as const)

export const defaultInstallmentFeeRateDimension = new InstallmentFeeRateDimension({
  name: 'defaultInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_default_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetInstallmentFeeRateDimension = new InstallmentFeeRateWithDefaultDimension({
  name: 'manuallySetInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isInstallmentFeeRateEditableDimension = new BooleanDimension({
  name: 'isInstallmentFeeRateEditable',
  displayKeys: { name: 'dimension_name_is_installment_fee_rate_editable' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcphInstallmentFeeRateDimension = new InstallmentFeeRateDimension({
  name: 'rcphInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_rcph_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
} as const)

export const defaultRcphInstallmentFeeRateDimension = new InstallmentFeeRateDimension({
  name: 'defaultRcphInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_default_rcph_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetRcphInstallmentFeeRateDimension = new InstallmentFeeRateWithDefaultDimension({
  name: 'manuallySetRcphInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_rcph_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
